import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BRAND } from './utils/theme';
import { CANONICAL_FIRM_ID } from './utils/firmScope.ts';
import { installNotificationOverrides } from './services/NotificationService.ts';
import { isManualPath } from './utils/manualRoute.ts';

const App = React.lazy(() => import('./App.tsx'));
const PublicESignPage = React.lazy(() => import('./components/PublicESignPage.tsx'));
const IntakeForm = React.lazy(() => import('./components/IntakeForm.tsx'));
const AuditorPortalPage = React.lazy(() => import('./modules/auditor/AuditorPortalPage.tsx'));
const PublicManualPage = React.lazy(() => import('./components/PublicManualPage.tsx'));

installNotificationOverrides();

const APP_CHUNK_RELOAD_KEY = 'app.chunk.reload.v1';

const shouldRecoverFromChunkError = (value: unknown): boolean => {
  const message = String((value as any)?.message || value || '').toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('mime type') ||
    message.includes('unexpected token <')
  );
};

const recoverFromChunkErrorOnce = () => {
  try {
    const hasRetried = window.sessionStorage.getItem(APP_CHUNK_RELOAD_KEY) === '1';
    if (hasRetried) return;
    window.sessionStorage.setItem(APP_CHUNK_RELOAD_KEY, '1');
  } catch {
    // Continue with reload if sessionStorage is unavailable.
  }

  window.location.reload();
};

window.addEventListener('error', (event) => {
  if (shouldRecoverFromChunkError((event as ErrorEvent)?.error || (event as ErrorEvent)?.message)) {
    recoverFromChunkErrorOnce();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (shouldRecoverFromChunkError((event as PromiseRejectionEvent)?.reason)) {
    recoverFromChunkErrorOnce();
  }
});

try {
  window.sessionStorage.removeItem(APP_CHUNK_RELOAD_KEY);
} catch {
  // Ignore storage access issues.
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const getPublicESignContext = () => {
  const params = new URLSearchParams(window.location.search);
  const isPublic = params.get('esignPublic') === '1' || (!!params.get('token') && !!params.get('esignRequest') && !!params.get('firmId'));
  if (!isPublic) {
    return null;
  }

  const requestId = String(params.get('esignRequest') || '').trim();
  const token = String(params.get('token') || '').trim();
  const publicFirmId = String(params.get('firmId') || '').trim();

  if (publicFirmId && publicFirmId !== CANONICAL_FIRM_ID) {
    console.warn('[ESign] Incoming firmId does not match canonical:', publicFirmId);
  }

  if (!requestId || !token || !publicFirmId) {
    return null;
  }

  return {
    firmId: publicFirmId,
    requestId,
    token,
    recipientEmail: String(params.get('recipient') || '').trim() || undefined,
  };
};

const getAuditorPortalContext = () => {
  const params = new URLSearchParams(window.location.search);
  const normalizedPathname = String(window.location.pathname || '').trim().toLowerCase().replace(/\/+$/, '') || '/';
  const normalizedHashPath = String(window.location.hash || '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase()
    .replace(/\/+$/, '');

  const isAuditorPortalPath =
    normalizedPathname === '/auditor' ||
    normalizedPathname === '/audit' ||
    normalizedHashPath === '/auditor' ||
    normalizedHashPath === '/audit' ||
    normalizedHashPath === 'auditor' ||
    normalizedHashPath === 'audit';

  const isAuditorPortal = params.get('auditorPortal') === '1' || params.get('auditor') === '1' || isAuditorPortalPath;
  if (!isAuditorPortal) return null;

  return {
    firmId: String(params.get('firmId') || '').trim() || CANONICAL_FIRM_ID,
  };
};

const getIntakeContext = () => {
  const normalizedPathname = String(window.location.pathname || '').trim().toLowerCase().replace(/\/+$/, '') || '/';
  const normalizedHashPath = String(window.location.hash || '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase()
    .replace(/\/+$/, '');

  const isIntakePath =
    normalizedPathname === '/intake' ||
    normalizedHashPath === '/intake' ||
    normalizedHashPath === 'intake';

  return isIntakePath;
};

const publicESignContext = getPublicESignContext();
const auditorPortalContext = getAuditorPortalContext();
const intakeContext = getIntakeContext();

const fallback = (
  <div className="flex items-center justify-center min-h-screen bg-slate-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND.primary }}></div>
  </div>
);

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <Suspense fallback={fallback}>
      {isManualPath(window.location.pathname) ? (
        <PublicManualPage />
      ) : auditorPortalContext ? (
        <AuditorPortalPage initialFirmId={auditorPortalContext.firmId} />
      ) : intakeContext ? (
        <IntakeForm />
      ) : publicESignContext ? (
        <PublicESignPage
          firmId={publicESignContext.firmId}
          requestId={publicESignContext.requestId}
          token={publicESignContext.token}
          recipientEmail={publicESignContext.recipientEmail}
        />
      ) : (
        <App />
      )}
    </Suspense>
  </React.StrictMode>
);